import { motion, AnimatePresence } from "motion/react";
import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import { buildObjectPositionClasses, type FocalMap } from "@/components/FocusedImage";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useHeroIntro, useMarkHeroIntroConsumed } from "@/hooks/useHeroIntro";
import { isTestMode } from "@/lib/testMode";

/**
 * Hero video/poster asset contract (keep in sync with `/public/videos/`):
 *
 *   • /videos/hero-poster-mobile.jpg  — poster served when viewport < 641px
 *   • /videos/hero-poster.jpg         — poster served when viewport ≥ 641px
 *   • /videos/hero-bg-mobile.mp4      — used on <1024px (mobile + tablet).
 *                                       Tablet has no dedicated variant; we
 *                                       deliberately fall back to the mobile
 *                                       encode because payload size beats
 *                                       resolution on that range.
 *   • /videos/hero-bg.mp4             — used on ≥1024px (desktop, single-shot).
 *
 * Both posters are `<link rel="preload">`-ed from index.html so they're the
 * LCP candidate. The <video> mounts AFTER first paint via requestIdleCallback,
 * uses `preload="none"` + `fetchPriority="low"`, and pauses when the tab is
 * hidden or the hero scrolls out of view to protect battery.
 *
 * Focal points — landscape source (3:2). Y drifts as container AR widens so
 * the central magic (sun rays + cat silhouette + light arcs) stays in frame.
 * Shared by the poster <img> and the looping <video>; both MUST use the exact
 * same string so cover-cropping stays pixel-synchronous when the video fades in.
 */
const HERO_FOCAL: FocalMap = {
  base: [50, 38],
  sm: [50, 36],
  md: [50, 34],
  lg: [50, 40],
  xl: [50, 42],
  "2xl": [50, 38],
};
const HERO_OBJECT_POSITION = buildObjectPositionClasses(HERO_FOCAL);
const HERO_FOCAL_HINT = `${HERO_FOCAL.lg![0]}%_${HERO_FOCAL.lg![1]}%`;
const HERO_END_POSTER_DESKTOP = "/videos/hero-poster-end.jpg";
const HERO_END_POSTER_MOBILE = "/videos/hero-poster-end-mobile.jpg";

// Detect data-saver / very slow networks — skip the video entirely and keep
// the (already-preloaded) poster on screen. Cheap enough to run once at
// module import; live reduced-motion state comes from useReducedMotion().
const shouldSkipVideo = (() => {
  if (typeof navigator === "undefined") return false;
  const conn = (navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string };
  }).connection;
  if (!conn) return false;
  if (conn.saveData) return true;
  if (conn.effectiveType === "slow-2g" || conn.effectiveType === "2g") return true;
  // 3G on a small viewport = choppy MPEG decode + heavy cost for negligible
  // gain over the poster. Desktop 3G is rare enough to keep the video path.
  if (
    conn.effectiveType === "3g" &&
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 767px)").matches
  ) {
    return true;
  }
  return false;
})();

// Pick mobile vs desktop video source once on mount.
// <1024px  → mobile encode (covers phone + tablet — see asset contract above)
// ≥1024px  → desktop encode
const isMobileViewport =
  typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;
const useMobileVideoSource =
  typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches;

const HeroSparkles = () => {
  const particles = [
    { left: "10%",  top: "32%", size: 6, delay: 0,  dur: 22, driftX: 40,  driftY: -60, color: "gold" },
    { left: "18%",  top: "55%", size: 5, delay: 3,  dur: 18, driftX: -30, driftY: -40, color: "peach" },
    { left: "26%",  top: "38%", size: 4, delay: 9,  dur: 24, driftX: 25,  driftY: -55, color: "gold" },
    { left: "32%",  top: "22%", size: 7, delay: 6,  dur: 26, driftX: 20,  driftY: 50,  color: "gold" },
    { left: "40%",  top: "62%", size: 5, delay: 2,  dur: 21, driftX: 30,  driftY: -70, color: "peach" },
    { left: "45%",  top: "42%", size: 4, delay: 8,  dur: 19, driftX: -25, driftY: -60, color: "gold" },
    { left: "52%",  top: "28%", size: 6, delay: 5,  dur: 23, driftX: 35,  driftY: -50, color: "peach" },
    { left: "58%",  top: "55%", size: 5, delay: 1,  dur: 20, driftX: -25, driftY: -70, color: "gold" },
    { left: "64%",  top: "38%", size: 7, delay: 7,  dur: 25, driftX: 30,  driftY: -45, color: "peach" },
    { left: "70%",  top: "60%", size: 4, delay: 4,  dur: 19, driftX: -40, driftY: -60, color: "gold" },
    { left: "76%",  top: "32%", size: 6, delay: 10, dur: 22, driftX: 35,  driftY: 40,  color: "peach" },
    { left: "82%",  top: "48%", size: 5, delay: 3,  dur: 21, driftX: -20, driftY: -55, color: "gold" },
    { left: "88%",  top: "40%", size: 8, delay: 6,  dur: 24, driftX: 25,  driftY: -50, color: "gold" },
    { left: "92%",  top: "58%", size: 4, delay: 9,  dur: 20, driftX: -30, driftY: -45, color: "peach" },
  ];

  const colorStyles = {
    gold: {
      background: "radial-gradient(circle, hsl(45 100% 88% / 1) 0%, hsl(38 95% 68% / 0.85) 35%, hsl(35 85% 55% / 0.4) 65%, transparent 80%)",
      glow: "hsl(38 95% 70% / 0.9)",
      glowOuter: "hsl(38 85% 65% / 0.5)",
    },
    peach: {
      background: "radial-gradient(circle, hsl(20 100% 92% / 1) 0%, hsl(15 90% 78% / 0.85) 35%, hsl(10 75% 65% / 0.4) 65%, transparent 80%)",
      glow: "hsl(15 90% 78% / 0.9)",
      glowOuter: "hsl(10 80% 70% / 0.5)",
    },
  };

  return (
    <>
      {particles.map((p, i) => (
        <motion.div
          key={i}
          className="pointer-events-none absolute rounded-full"
          style={{
            left: p.left,
            top: p.top,
            width: p.size,
            height: p.size,
            background: colorStyles[p.color].background,
            boxShadow: `0 0 ${p.size * 5}px ${colorStyles[p.color].glow}, 0 0 ${p.size * 10}px ${colorStyles[p.color].glowOuter}`,
          }}
          initial={{ opacity: 0, x: 0, y: 0 }}
          animate={{
            opacity: [0, 0.9, 0.9, 0],
            x: [0, p.driftX, p.driftX * 0.7, p.driftX * 1.2],
            y: [0, p.driftY, p.driftY * 1.4, p.driftY * 1.8],
          }}
          transition={{
            duration: p.dur,
            delay: p.delay,
            repeat: Infinity,
            ease: "easeInOut",
            times: [0, 0.1, 0.9, 1],
          }}
        />
      ))}
    </>
  );
};

const HeroSunRays = () => {
  return (
    <>
      {/* Primary sun ray — lange smalle band vanaf de zon (top-center)
          langzaam roterend en pulserend */}
      <motion.div
        className="pointer-events-none absolute"
        style={{
          top: "-10%",
          left: "35%",
          width: "30%",
          height: "130%",
          background: "linear-gradient(180deg, hsl(45 90% 78% / 0.18) 0%, hsl(40 85% 72% / 0.10) 40%, hsl(35 80% 65% / 0.04) 75%, transparent 100%)",
          filter: "blur(24px)",
          transformOrigin: "top center",
          mixBlendMode: "screen",
        }}
        initial={{ opacity: 0, rotate: -8, scaleX: 0.9 }}
        animate={{
          opacity: [0.6, 1, 0.7, 0.9, 0.6],
          rotate: [-8, -2, -8],
          scaleX: [0.9, 1.15, 0.95, 1.1, 0.9],
        }}
        transition={{
          opacity: { duration: 42, repeat: Infinity, ease: "easeInOut" },
          rotate: { duration: 42, repeat: Infinity, ease: "easeInOut" },
          scaleX: { duration: 42, repeat: Infinity, ease: "easeInOut" },
        }}
      />
      {/* Secondary sun ray — kleinere band aan de andere kant,
          asynchroon met de primaire voor natuurlijk gevoel */}
      <motion.div
        className="pointer-events-none absolute"
        style={{
          top: "-5%",
          left: "55%",
          width: "22%",
          height: "115%",
          background: "linear-gradient(180deg, hsl(42 92% 82% / 0.14) 0%, hsl(38 85% 70% / 0.07) 45%, transparent 90%)",
          filter: "blur(18px)",
          transformOrigin: "top center",
          mixBlendMode: "screen",
        }}
        initial={{ opacity: 0.5, rotate: 6, scaleX: 1 }}
        animate={{
          opacity: [0.5, 0.9, 0.6, 0.85, 0.5],
          rotate: [6, 1, 6],
          scaleX: [1, 0.85, 1.1, 0.9, 1],
        }}
        transition={{
          opacity: { duration: 38, delay: 8, repeat: Infinity, ease: "easeInOut" },
          rotate: { duration: 38, delay: 8, repeat: Infinity, ease: "easeInOut" },
          scaleX: { duration: 38, delay: 8, repeat: Infinity, ease: "easeInOut" },
        }}
      />
    </>
  );
};

const HomeHero = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [, setVideoLoaded] = useState(false);
  // Defer mounting the <video> until after first paint so the preloaded
  // poster <img> becomes the LCP element. This eliminates the ~3s resource
  // load delay Lighthouse attributed to the video.
  const [videoMounted, setVideoMounted] = useState(false);
  // Desktop-only flag (lg breakpoint = 1024px). Used to switch the hero
  // video into a single-shot, hold-on-final-frame playback mode and to gate
  // the desktop mist overlay. Mobile + tablet behavior is fully preserved.
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );
  // Tracks whether the desktop video has reached its final frame. We use this
  // to fade out decorative desktop-only motion (mist drift, warm pulse) so the
  // final frame reads as an intentional, calm tableau.
  const [desktopVideoEnded, setDesktopVideoEnded] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(min-width: 1024px)");
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = isDesktop ? HERO_END_POSTER_DESKTOP : HERO_END_POSTER_MOBILE;
  }, [isDesktop]);
  const prefersReducedMotion = useReducedMotion();
  // Test-modus = visuele regressie: behandel als reduced motion zodat snapshots
  // altijd op het posterframe vastliggen en geen entrance-animaties flakkeren.
  const testMode = isTestMode();
  const freezeMotion = prefersReducedMotion || testMode;

  // Coordinated mobile intro choreography — see useHeroIntro. Only true on
  // the very first hero mount of the tab session, on route `/`, when the
  // user hasn't opted out of motion. Desktop keeps its own card entrance.
  const playIntro = useHeroIntro();
  useMarkHeroIntroConsumed(playIntro);
  const mobileIntro = playIntro && !isDesktop && !freezeMotion;

  // Scroll-hint fade: once the user scrolls > 60px past the top the decorative
  // chevron fades out. rAF-throttled scroll listener; no layout reads besides
  // window.scrollY so it's cheap even on low-end phones.
  const [scrolledPastHint, setScrolledPastHint] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    let ticking = false;
    const check = () => {
      ticking = false;
      setScrolledPastHint(window.scrollY > 60);
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(check);
    };
    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Reduced-motion users krijgen géén autoplay-video — alleen de stille poster.
  // Dit voorkomt parallax-/loop-beweging die misselijkheid kan veroorzaken.
  // Test-only override: ?heroForceVideo=1 dwingt de <video> render-pad af zodat
  // visual-regression specs poster vs video alignment kunnen vergelijken zonder
  // de runtime defaults aan te passen. Heeft geen effect zonder testMode-flag.
  const forceVideoForTest =
    testMode &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("heroForceVideo") === "1";
  const useStaticPoster =
    !forceVideoForTest && (shouldSkipVideo || freezeMotion);

  // Fade the <video> in over 600ms once its first frame is decoded so the
  // handover from poster → video is never a hard swap on mobile. The poster
  // <img> stays mounted underneath, so there is never a black frame.
  const [videoFadedIn, setVideoFadedIn] = useState(false);

  // Lazy-start video playback after first frame renders. `play()` returns a
  // promise that rejects when the browser blocks autoplay — swallow it so we
  // don't emit an unhandled rejection; the poster remains visible and that's
  // an acceptable fallback.
  useEffect(() => {
    if (useStaticPoster) return;
    const video = videoRef.current;
    if (!video) return;
    const onCanPlay = () => {
      setVideoLoaded(true);
      const p = video.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    video.addEventListener("canplay", onCanPlay, { once: true });
    return () => video.removeEventListener("canplay", onCanPlay);
  }, [useStaticPoster, videoMounted]);

  // Mount the <video> after first paint (idle callback) so the poster <img>
  // is the LCP candidate and the video fetch never blocks LCP.
  useEffect(() => {
    if (useStaticPoster) return;
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const schedule = w.requestIdleCallback
      ? (cb: () => void) => w.requestIdleCallback!(cb, { timeout: 1500 })
      : (cb: () => void) => window.setTimeout(cb, 400);
    const id = schedule(() => setVideoMounted(true));
    return () => {
      if (typeof id === "number") clearTimeout(id);
    };
  }, [useStaticPoster]);

  // Battery-friendly: pause the video when the tab is hidden or the hero
  // scrolls off screen. Resume automatically when it becomes visible again.
  // Cheap: two listeners + one IntersectionObserver on a single element.
  useEffect(() => {
    if (useStaticPoster) return;
    const video = videoRef.current;
    if (!video) return;

    const safePlay = () => {
      const p = video.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    const safePause = () => {
      try {
        video.pause();
      } catch {
        /* noop */
      }
    };

    let inView = true;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") safePause();
      else if (inView) safePlay();
    };
    document.addEventListener("visibilitychange", onVisibility);

    const section = video.closest("[data-hero]");
    let io: IntersectionObserver | undefined;
    if (section && "IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            inView = entry.isIntersecting;
            if (!inView) safePause();
            else if (document.visibilityState !== "hidden") safePlay();
          }
        },
        { threshold: 0 },
      );
      io.observe(section);
    }

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      io?.disconnect();
    };
  }, [useStaticPoster, videoMounted]);

  const videoSrc = useMobileVideoSource
    ? "/videos/hero-bg-mobile.mp4"
    : "/videos/hero-bg.mp4";
  const posterSrc = isMobileViewport
    ? "/videos/hero-poster-mobile.jpg"
    : "/videos/hero-poster.jpg";

  return (
    <section data-hero className="relative isolate w-full max-w-none overflow-hidden h-[100svh] min-h-[520px] max-h-[900px] min-[769px]:h-[72svh] min-[769px]:min-h-[520px] min-[769px]:max-h-[720px] lg:h-[88svh] lg:min-h-[600px] lg:max-h-[940px]">
      <div className="absolute inset-0 z-0 h-full w-full overflow-hidden bg-background">
        {/* Video layer — poster provides instant visual, video loads async */}
        <motion.div
          className="absolute inset-0 h-full w-full will-change-transform"
          // Mobile intro (fresh session, route "/"): tightened choreography —
          // scale 1.04 → 1.00 over 900ms starting at 100ms so poster/video feel
          // like they "settle" onto the page before the navbar / hint join in.
          // Desktop keeps the original 3.5s cinematic breath.
          // Return visits (playIntro=false) skip the entrance entirely on
          // mobile so back-navigating to `/` doesn't replay the sequence.
          initial={
            freezeMotion
              ? false
              : mobileIntro
                ? { scale: 1.04, opacity: 0 }
                : !isDesktop && !playIntro
                  ? false
                  : { scale: 1.06, opacity: 0 }
          }
          animate={{ scale: 1, opacity: 1 }}
          transition={
            mobileIntro
              ? { duration: 0.9, delay: 0.1, ease: [0.16, 0.68, 0.35, 1] }
              : { duration: 3.5, ease: [0.22, 0.68, 0.35, 1] }
          }
        >
          {/* Always render the poster <img> first — it's preloaded with
              fetchpriority=high, so it becomes the LCP element instantly.
              The <video> mounts after first paint and visually replaces it. */}
          <img
            src={posterSrc}
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            data-focus={HERO_FOCAL_HINT}
            data-hero-media
            className={`pointer-events-none absolute inset-0 h-full w-full object-cover ${HERO_OBJECT_POSITION}`}
          />
          {!useStaticPoster && videoMounted && (
            <video
              ref={videoRef}
              autoPlay
              muted
              loop={!isDesktop}
              playsInline
              // preload="none" + low fetch priority: the poster carries LCP;
              // the video must never race the LCP paint or steal main-thread
              // budget before first contentful frame.
              preload="none"
              // @ts-expect-error – valid HTML attr, missing from lib.dom typings
              fetchpriority="low"
              poster={posterSrc}
              disablePictureInPicture
              aria-hidden="true"
              data-focus={HERO_FOCAL_HINT}
              data-hero-media
              onLoadedData={() => setVideoFadedIn(true)}
              onEnded={() => {
                // Desktop-only: stop on the final frame, no loop, no reset.
                if (!isDesktop) return;
                const v = videoRef.current;
                if (!v) return;
                try {
                  v.pause();
                } catch {
                  /* noop */
                }
                setDesktopVideoEnded(true);
              }}
              className={`pointer-events-none absolute inset-0 h-full w-full object-cover ${HERO_OBJECT_POSITION}`}
              // Desktop-only filmic grade: very subtle contrast/saturation lift
              // + tiny warm tint. Mobile/tablet keep the original raw video so
              // their look is bit-identical to the previous build. GPU-cheap
              // (single CSS filter), no layout impact.
              style={{
                viewTransitionName: "hero-media",
                opacity: videoFadedIn ? 1 : 0,
                transition: "opacity 600ms ease-out",
                ...(isDesktop
                  ? { filter: "contrast(1.06) saturate(1.05) brightness(1.01)" }
                  : {}),
              }}
            >
              <source src={videoSrc} type="video/mp4" />
            </video>
          )}
        </motion.div>

        {isDesktop && (
          <motion.img
            src={isDesktop ? HERO_END_POSTER_DESKTOP : HERO_END_POSTER_MOBILE}
            alt=""
            aria-hidden="true"
            initial={{ opacity: 0, scale: 1, x: 0 }}
            animate={
              desktopVideoEnded
                ? { opacity: 1, scale: 1.02, x: -8 }
                : { opacity: 0, scale: 1, x: 0 }
            }
            transition={{
              opacity: { duration: 1.2, ease: "easeOut" },
              scale: { duration: 30, ease: "easeInOut", repeat: Infinity, repeatType: "reverse" },
              x: { duration: 30, ease: "easeInOut", repeat: Infinity, repeatType: "reverse" },
            }}
            className={`pointer-events-none absolute inset-0 h-full w-full object-cover z-[5] ${HERO_OBJECT_POSITION}`}
            style={{ filter: "contrast(1.06) saturate(1.05) brightness(1.01)" }}
          />
        )}

        {isDesktop && desktopVideoEnded && !freezeMotion && (
          <motion.div
            className="absolute inset-0 z-[5] pointer-events-none overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 3, delay: 1.5, ease: "easeOut" }}
          >
            <HeroSunRays />
          </motion.div>
        )}

        <AnimatePresence>
          {isDesktop && desktopVideoEnded && !freezeMotion && (
            <motion.div
              key="hero-sparkles"
              className="absolute inset-0 z-[6] pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 2, delay: 1.2, ease: "easeOut" }}
            >
              <HeroSparkles />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Top scrim for navbar readability — very subtle on mobile to avoid a visible dark band */}
        <div className="absolute inset-x-0 top-0 z-[11] h-16 sm:h-32 pointer-events-none bg-gradient-to-b from-black/8 via-black/2 to-transparent sm:from-black/20 sm:via-black/6" />

        {/* Combined overlay — single animated layer instead of multiple */}
        <motion.div
          className="absolute inset-0 z-10 pointer-events-none"
          initial={freezeMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2.5, delay: 0.4, ease: [0.22, 0.68, 0.35, 1] }}
        >
          {/* Mobile uses a stronger bottom-fade so the title reads cleanly */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/55 sm:from-black/45 sm:via-black/20 sm:to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_72%_60%_at_50%_45%,rgba(0,0,0,0.06)_0%,rgba(0,0,0,0.18)_100%)] sm:bg-[radial-gradient(ellipse_70%_60%_at_50%_45%,rgba(0,0,0,0.18)_0%,rgba(0,0,0,0.3)_100%)]" />
          {/* Vignette — merged into same animated container */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_70%_at_50%_50%,transparent_45%,rgba(0,0,0,0.12)_100%)]" />
        </motion.div>

        {/* Warm glow pulse — tablet only (sm→lg). On desktop (lg+) the new
            cinematic mist stack below replaces it for a softer, more layered
            feel. Skipped entirely under reduced-motion. */}
        {!freezeMotion && (
          <motion.div
            className="absolute inset-0 z-10 pointer-events-none hidden sm:block lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.6, 0.4, 0.6] }}
            transition={{
              opacity: { duration: 8, delay: 1.5, repeat: Infinity, ease: "easeInOut" },
            }}
          >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_45%,hsl(32_35%_52%_/_0.08)_0%,transparent_70%)]" />
          </motion.div>
        )}

        {/* Desktop-only cinematic mist (lg+) — three soft, layered warm
            radial gradients that drift slowly on the GPU (transform + opacity
            only). Subtle by design: behind the text, never over it. Fades out
            gracefully once the hero video has held its final frame so the
            tableau reads as intentional and calm. */}
        {!freezeMotion && (
          <motion.div
            className="absolute inset-0 z-10 pointer-events-none hidden lg:block"
            initial={{ opacity: 0 }}
            animate={{ opacity: desktopVideoEnded ? 0.55 : 1 }}
            transition={{ duration: desktopVideoEnded ? 2.4 : 2.0, delay: desktopVideoEnded ? 0 : 0.6, ease: [0.22, 0.68, 0.35, 1] }}
          >
            {/* Foreground mist — warm ivory drift, slow horizontal */}
            <motion.div
              className="absolute -inset-x-[12%] inset-y-0 will-change-transform"
              animate={desktopVideoEnded ? { x: 0 } : { x: ["-3%", "3%", "-3%"] }}
              transition={{ duration: 28, repeat: desktopVideoEnded ? 0 : Infinity, ease: "easeInOut" }}
              style={{
                background:
                  "radial-gradient(ellipse 55% 38% at 38% 58%, hsl(var(--accent) / 0.10) 0%, transparent 65%)",
              }}
            />
            {/* Midground mist — golden ivory pool, vertical drift */}
            <motion.div
              className="absolute -inset-y-[10%] inset-x-0 will-change-transform"
              animate={desktopVideoEnded ? { y: 0 } : { y: ["1.5%", "-1.5%", "1.5%"] }}
              transition={{ duration: 36, repeat: desktopVideoEnded ? 0 : Infinity, ease: "easeInOut" }}
              style={{
                background:
                  "radial-gradient(ellipse 65% 45% at 62% 42%, hsl(var(--primary) / 0.07) 0%, transparent 70%)",
              }}
            />
            {/* Background depth — very wide, very faint warm wash */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse 90% 70% at 50% 35%, hsl(var(--accent) / 0.045) 0%, transparent 75%)",
              }}
            />
          </motion.div>
        )}

        {/* Desktop-only volumetric forest fog (lg+) — replaces the perception
            of a flat white mist band with multiple feathered, warm-ivory fog
            layers that drift horizontally and breathe vertically at different
            speeds. Pure transform/opacity animation, no layout impact.
            Skipped under reduced motion. */}
        {!freezeMotion && (
          <motion.div
            className="absolute inset-x-0 bottom-0 z-[11] h-[55%] pointer-events-none hidden lg:block overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: desktopVideoEnded ? 0.85 : 1 }}
            transition={{ duration: 3.2, delay: 0.8, ease: [0.22, 0.68, 0.35, 1] }}
            aria-hidden
          >
            {/* Layer A — dense low foreground fog, slow horizontal drift */}
            <motion.div
              className="absolute -inset-x-[18%] bottom-[-8%] h-[70%] will-change-transform"
              animate={{ x: ["-4%", "4%", "-4%"] }}
              transition={{ duration: 42, repeat: Infinity, ease: "easeInOut" }}
              style={{
                background:
                  "radial-gradient(ellipse 60% 70% at 30% 90%, hsl(var(--background) / 0.55) 0%, hsl(var(--background) / 0.18) 40%, transparent 75%), radial-gradient(ellipse 55% 65% at 70% 95%, hsl(var(--background) / 0.45) 0%, hsl(var(--background) / 0.14) 45%, transparent 78%)",
                filter: "blur(2px)",
              }}
            />
            {/* Layer B — wider transparent fog passing behind logo area */}
            <motion.div
              className="absolute -inset-x-[14%] bottom-[6%] h-[60%] will-change-transform"
              animate={{ x: ["3%", "-3%", "3%"], y: ["1%", "-1.5%", "1%"] }}
              transition={{ duration: 56, repeat: Infinity, ease: "easeInOut" }}
              style={{
                background:
                  "radial-gradient(ellipse 70% 55% at 50% 70%, hsl(38 28% 90% / 0.32) 0%, hsl(38 28% 90% / 0.08) 50%, transparent 80%)",
                filter: "blur(3px)",
              }}
            />
            {/* Layer C — thin atmospheric haze around mid-trees */}
            <motion.div
              className="absolute inset-x-0 top-0 h-[55%] will-change-transform"
              animate={{ x: ["-2%", "2%", "-2%"] }}
              transition={{ duration: 70, repeat: Infinity, ease: "easeInOut" }}
              style={{
                background:
                  "radial-gradient(ellipse 80% 50% at 50% 50%, hsl(var(--accent) / 0.06) 0%, transparent 70%)",
                filter: "blur(2px)",
              }}
            />
            {/* Layer D — drifting wisps, very slow, subtle vertical breathing */}
            <motion.div
              className="absolute -inset-x-[10%] bottom-[20%] h-[35%] will-change-transform"
              animate={{ y: ["2%", "-2%", "2%"], opacity: [0.55, 0.75, 0.55] }}
              transition={{ duration: 48, repeat: Infinity, ease: "easeInOut" }}
              style={{
                background:
                  "radial-gradient(ellipse 35% 45% at 22% 60%, hsl(38 28% 92% / 0.22) 0%, transparent 70%), radial-gradient(ellipse 30% 40% at 78% 50%, hsl(38 28% 92% / 0.18) 0%, transparent 72%)",
                filter: "blur(4px)",
              }}
            />
          </motion.div>
        )}

        {/* Desktop-only firefly particles (lg+) — extremely sparse, warm
            ivory-gold, slow independent drift. Positioned in the darker
            forest zones, never above the navbar (top 18%+). Pure transform +
            opacity. Disabled under reduced motion. */}
        {!freezeMotion && (
          <div
            className="absolute inset-0 z-[12] pointer-events-none hidden lg:block overflow-hidden"
            aria-hidden
          >
            {[
              { left: "12%", top: "48%", size: 4, delay: 0,   dur: 11, drift: 18 },
              { left: "22%", top: "62%", size: 3, delay: 2.5, dur: 13, drift: 14 },
              { left: "78%", top: "44%", size: 4, delay: 1.2, dur: 12, drift: -16 },
              { left: "88%", top: "58%", size: 3, delay: 4,   dur: 14, drift: 12 },
              { left: "8%",  top: "72%", size: 3, delay: 3.5, dur: 15, drift: -12 },
              { left: "92%", top: "70%", size: 3, delay: 5.5, dur: 13, drift: 16 },
              { left: "32%", top: "38%", size: 2, delay: 6,   dur: 16, drift: 10 },
              { left: "68%", top: "36%", size: 2, delay: 1.8, dur: 14, drift: -10 },
            ].map((f, i) => (
              <motion.span
                key={i}
                className="absolute rounded-full will-change-transform"
                style={{
                  left: f.left,
                  top: f.top,
                  width: f.size,
                  height: f.size,
                  background:
                    "radial-gradient(circle, hsl(38 65% 78% / 0.85) 0%, hsl(32 35% 52% / 0.35) 50%, transparent 75%)",
                  boxShadow: "0 0 8px hsl(38 65% 75% / 0.45), 0 0 16px hsl(32 35% 52% / 0.18)",
                }}
                animate={{
                  y: [0, -f.drift * 0.6, f.drift * 0.4, 0],
                  x: [0, f.drift * 0.5, -f.drift * 0.3, 0],
                  opacity: [0, 0.7, 0.35, 0.65, 0],
                }}
                transition={{
                  duration: f.dur,
                  delay: f.delay,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            ))}
          </div>
        )}

        {/* Desktop-only soft cinematic vignette (lg+) — adds depth separation
            between foreground mist and background trees without darkening the
            center. Static, zero animation cost. */}
        <div
          className="absolute inset-0 z-[11] pointer-events-none hidden lg:block"
          aria-hidden
          style={{
            background:
              "radial-gradient(ellipse 95% 80% at 50% 45%, transparent 55%, hsl(25 25% 8% / 0.18) 100%)",
          }}
        />
      </div>

      {/*
        Responsieve top-offset = navbar (60–68px) + safe-area + ademruimte.
        - <360px:  76px → eyebrow blijft volledig zichtbaar onder de compacte navbar
        - ≥360px:  88px → ruimere lucht boven de eyebrow op standaard mobiel
        - ≥431px: 104px → past bij grotere telefoons / hogere navbar-presence
        - ≥640px: 120px → tablet-formaat met meer cinematische headroom
        Bottom-padding schaalt mee zodat de CTA nooit tegen de viewport-rand of
        een mobiele browser-UI plakt.
      */}
      {/*
        The hero renders ONLY the video/poster on every breakpoint. The glass
        card is kept in the DOM (sr-only) so search engines and screen readers
        still receive the H1 + supporting copy + primary links, but it is
        never visually rendered — the desktop "Neem contact op" affordance now
        lives in the Navbar (see TrackedGlowCta with trackLabel="nav_desktop_cta").
      */}
      <div data-hero-card className="sr-only">
        <div className="relative z-20 mx-auto flex h-full w-full max-w-3xl flex-col items-center justify-center px-4 pt-[calc(env(safe-area-inset-top,0px)+88px)] pb-10 text-center min-[431px]:px-5 min-[431px]:pt-[calc(env(safe-area-inset-top,0px)+104px)] min-[431px]:pb-12 sm:pt-[calc(env(safe-area-inset-top,0px)+120px)] sm:pb-16">
          <div
            className="w-full rounded-[1.75rem] border border-white/20 bg-background/84 px-5 py-5 text-left shadow-[0_10px_40px_-18px_rgba(0,0,0,0.35)] backdrop-blur-xl min-[431px]:py-6"
          >
            {/* LCP-critical text — rendered eagerly without opacity:0 entrance so
                it paints on the first frame (instead of waiting for JS hydration
                + a 1.3s stagger delay). Preserves visual identity; only the CTA
                row keeps a subtle fade-in for cinematic warmth. */}
            <p
              className="text-[10.5px] font-body tracking-[0.2em] uppercase text-accent mb-2.5 min-[431px]:text-[11px] min-[431px]:tracking-[0.22em] min-[431px]:mb-3"
            >
              Praktijk Sprankelien · Ramsel
            </p>
            <h1
              className="font-heading italic text-foreground text-[2rem] leading-[1.12] tracking-tight max-w-[13ch] min-[431px]:text-[2.125rem] min-[431px]:leading-[1.08] min-[431px]:max-w-[12ch]"
            >
              Een warme plek om te sprankelen
            </h1>
            <p
              className="mt-3 max-w-[34ch] text-[15px] font-body leading-[1.6] text-foreground/90 min-[431px]:mt-4 min-[431px]:text-[16px] min-[431px]:leading-[1.6] min-[431px]:max-w-[30ch]"
            >
              Speltherapie, kinderyoga &amp; zomerkampen — met zachtheid en aandacht voor elk kind.
            </p>
            <motion.div
              initial={freezeMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.4, ease: [0.22, 0.68, 0.35, 1] }}
              className="mt-6 flex flex-col gap-3 min-[431px]:flex-row min-[431px]:items-center"
            >
              <Link
                to="/contact"
                className="btn-premium relative inline-flex min-h-[52px] items-center justify-center overflow-hidden rounded-full bg-primary px-7 text-[15px] font-semibold text-primary-foreground"
              >
                <span className="absolute inset-0 -translate-x-full animate-[shimmer_4.5s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/12 to-transparent" />
                <span className="relative">Neem contact op</span>
              </Link>
              <Link
                to="/over-mij"
                className="inline-flex min-h-[52px] items-center gap-2 self-start text-[15px] font-semibold text-foreground/78"
              >
                Ontdek wie we zijn
                <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll hint — decorative chevron, mobile/tablet only (< lg). Sits above
          the sticky mobile CTA + safe-area, fades once the user starts scrolling.
          Hidden entirely at ≥lg where the glass card takes over the affordance. */}
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 z-20 -translate-x-1/2 lg:hidden"
        style={{
          bottom:
            "calc(env(safe-area-inset-bottom, 0px) + var(--mobile-cta-h, 0px) + 88px)",
          color: "hsl(0 0% 100% / 0.65)",
          textShadow: "0 1px 8px rgba(0,0,0,0.4)",
        }}
        // Fresh mobile session: rise from below (y 12 → 0, opacity 0 → 1)
        // 900ms into the sequence over 700ms. Return visits skip the rise and
        // just track the scroll fade-out. Icon color already carries 0.65 alpha
        // so wrapper opacity 1 = the visual "0.65" target from the spec.
        initial={mobileIntro ? { y: 12, opacity: 0 } : false}
        animate={{ y: 0, opacity: scrolledPastHint ? 0 : 1 }}
        transition={
          mobileIntro
            ? { duration: 0.7, delay: 0.9, ease: [0.22, 0.68, 0.35, 1] }
            : { duration: 0.6, ease: [0.22, 0.68, 0.35, 1] }
        }
      >
        {freezeMotion ? (
          <ChevronDown size={24} strokeWidth={1.5} style={{ opacity: 0.7 }} />
        ) : (
          <motion.span
            className="inline-flex"
            animate={{ y: [0, 8, 0], opacity: [0.5, 0.9, 0.5] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          >
            <ChevronDown size={24} strokeWidth={1.5} />
          </motion.span>
        )}
      </motion.div>
    </section>
  );
};

export default HomeHero;
